import React, { useState } from 'react';
import useTranslation from '../../../../../../Hooks/useTranslation';
import { NCZIPreferredHealthProfessional } from '../../../../Interfaces/ncziPatientInformations';
import { Theme } from '../../../../../../Enums/Theme.enum';
import LinkButton from '../../../../../../Components/Button/LinkButton';
import NCZIEditPreferredHealthProfessionalModal from '../EditPreferredHealthProfessional/NCZIEditPreferredHealthProfessionalModal';
import { StornoPatientSummaryContactInformation } from '../../../../Commands/StornoPatientSummaryContactInformation';
import NCZICommandResult from '../../../NCZICommandResult/NCZICommandResult';
import useNCZICommandProcess from '../../../../Hooks/useNCZICommandProcess';
// @ts-ignore
import styles from './ElectronicHealthCardContactInfoPreferredHealthProfessional.module.scss';

import NZCIListItem from '../../../Base/NZCIListItem';
import { FormattedDateTime } from '../../../../../../Components/FormattedDate';
import RemoveButton from '../../../../../../Components/Button/RemoveButton';

interface Props {
	healthProfessional: NCZIPreferredHealthProfessional;
	patientIdentifier: string;
	handleReloadData?: () => void;
}

const ElectronicHealthCardContactInfoPreferredHealthProfessional: React.FC<
	Props
> = props => {
	const { healthProfessional, patientIdentifier, handleReloadData } = props;
	const { t } = useTranslation();
	const [editModalOpened, setEditModalOpened] = useState<boolean>(false);
	const { processingCommandId, processCommand, isProcessing } =
		useNCZICommandProcess({
			onFinish: () => handleReloadData?.(),
		});

	const handleCloseModal = (reloadData: boolean) => {
		setEditModalOpened(false);
		if (reloadData) {
			handleReloadData?.();
		}
	};

	const handleStornoRecord = async () => {
		const stornoCommand = StornoPatientSummaryContactInformation({
			patientCode: patientIdentifier,
			recordIds: [healthProfessional.rcId],
		});

		await processCommand(stornoCommand);
	};

	if (isProcessing && processingCommandId) {
		return <NCZICommandResult commandId={processingCommandId} />;
	}

	return (
		<>
			<div className={styles.wrapper}>
				<div className={styles.header}>
					<div className={styles.title}>{healthProfessional.fullName}</div>
					<div className={styles.controls}>
						<LinkButton
							title={'base.shared.edit'}
							theme={Theme.BLUE}
							firstUpper
							onClick={() => setEditModalOpened(true)}
						/>
						<RemoveButton
							title={'nczi.action.delete'}
							onClick={() => handleStornoRecord()}
						/>
					</div>
				</div>
				<div className={styles.content}>
					{healthProfessional.createdAt && (
						<NZCIListItem
							title='nczi.createdAt'
							value={
								<FormattedDateTime datetime={healthProfessional.createdAt} />
							}
							align
						/>
					)}
					{healthProfessional.note && (
						<NZCIListItem
							title='base.internalNote'
							value={healthProfessional.note}
							align
						/>
					)}
					{healthProfessional?.address && (
						<NZCIListItem
							title='base.addressOfLiving'
							value={
								<>
									{healthProfessional.address.street}{' '}
									{healthProfessional.address.descriptiveNumber}/
									{healthProfessional.address.guideNumber}
								</>
							}
							align
						/>
					)}
					{healthProfessional?.address?.city?.displayName && (
						<NZCIListItem
							title='base.shared.city'
							value={healthProfessional.address.city.displayName}
							align
						/>
					)}
					{healthProfessional?.address?.district?.displayName && (
						<NZCIListItem
							title='component.nczi.ezk.district'
							value={healthProfessional.address.district.displayName}
							align
						/>
					)}
					{healthProfessional?.address?.region?.displayName && (
						<NZCIListItem
							title='component.nczi.ezk.region'
							value={healthProfessional.address.region.displayName}
							align
						/>
					)}
					{healthProfessional?.address?.postCode?.displayName && (
						<NZCIListItem
							title='base.shared.postCode'
							value={healthProfessional.address.postCode.displayName}
							align
						/>
					)}
				</div>
			</div>
			<NCZIEditPreferredHealthProfessionalModal
				preferredHealthProfessional={healthProfessional}
				patientIdentifier={patientIdentifier}
				opened={editModalOpened}
				onClose={handleCloseModal}
			/>
		</>
	);
};

export default ElectronicHealthCardContactInfoPreferredHealthProfessional;
